import styled, { keyframes, css } from 'styled-components'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const BuyOrderRow = styled.tr<{ isExpanded?: boolean }>`
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #FAFAFA;
  }

  ${props => props.isExpanded && css`
    background-color: #F5F9FF;
  `}
`

export const BuyOrderExpandedRow = styled.tr`
  animation: ${fadeIn} 0.2s ease forwards;
`

export const BuyOrderExpandedCell = styled.td`
  padding: 0 !important;
  border-top: none;
`

export const BuyOrderPanel = styled.div`
  padding: 16px 20px;
  background: #FAFAFA;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  margin: 4px 0 8px 0;
`

export const BuyOrderPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
`

export const BuyOrderPanelTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #303030;
  display: flex;
  align-items: center;
  gap: 10px;
`

export const BuyOrderForm = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const FormLabel = styled.label`
  font-size: 11px;
  font-weight: 500;
  color: #696969;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

export const FormStaticValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #303030;
  min-height: 20px;
  display: inline-block;
`

const inputBase = css`
  padding: 10px 12px;
  border: 1px solid #D9D9D9;
  border-radius: 6px;
  font-size: 14px;
  color: #303030;
  background: #FFFFFF;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #0090D3;
    box-shadow: 0 0 0 2px rgba(0, 144, 211, 0.12);
  }

  &:disabled {
    background: #F5F5F5;
    color: #8C8C8C;
    cursor: not-allowed;
  }
`

export const FormInput = styled.input`
  ${inputBase}
`

export const FormTextarea = styled.textarea`
  ${inputBase}
  resize: vertical;
  min-height: 60px;
`

export const FormSelect = styled.select`
  ${inputBase}
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23696969' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;
`

export const BuyOrderActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
`

export const ActionButton = styled.button<{ variant?: 'primary' | 'success' | 'danger' | 'ghost' | 'outline' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid transparent;
  white-space: nowrap;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${props => {
    switch (props.variant) {
      case 'primary':
        return css`
          background: #0090D3;
          color: #FFFFFF;
          border-color: #0090D3;
          &:hover:not(:disabled) { background: #0078B0; border-color: #0078B0; }
        `
      case 'success':
        return css`
          background: #16A34A;
          color: #FFFFFF;
          border-color: #16A34A;
          &:hover:not(:disabled) { background: #128A3D; border-color: #128A3D; }
        `
      case 'danger':
        return css`
          background: #FFFFFF;
          color: #CF1322;
          border-color: #CF1322;
          &:hover:not(:disabled) { background: #FFF1F0; }
        `
      case 'outline':
        return css`
          background: #FFFFFF;
          color: #0090D3;
          border-color: #0090D3;
          &:hover:not(:disabled) { background: #EAF6FF; }
        `
      default:
        return css`
          background: transparent;
          color: #595959;
          border-color: #D9D9D9;
          &:hover:not(:disabled) { background: #FAFAFA; }
        `
    }
  }}
`

const statusPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`

export const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;
  }

  ${props => {
    switch (props.status) {
      case 'PENDING':
        return css`
          background: #FFF7E6;
          color: #D48806;
          &::before { background: #D48806; animation: ${statusPulse} 2s ease infinite; }
        `
      case 'DELIVERED':
        return css`
          background: #E6F7FF;
          color: #0070C0;
          &::before { background: #0070C0; }
        `
      case 'COMPLETED':
        return css`
          background: #F0FFF4;
          color: #16A34A;
          &::before { background: #16A34A; }
        `
      case 'CANCELLED':
        return css`
          background: #FFF1F0;
          color: #CF1322;
          &::before { background: #CF1322; }
        `
      default:
        return css`
          background: #F5F5F5;
          color: #8C8C8C;
          &::before { background: #8C8C8C; }
        `
    }
  }}
`

export const OcBadgeCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`

export const CreateOcButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  background: #F0F9FF;
  color: #0090D3;
  border: 1px dashed #0090D3;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: #E0F3FF;
    border-style: solid;
  }
`

export const ObservationsRow = styled.div`
  grid-column: 1 / -1;
`
